import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import cx from 'clsx';
import message from 'antd/es/message';

import { useCurrentAddressInfo } from '@/hooks';
import { Input, Button, Loading, TokenAvatar } from '@/components';
import { AntdSelect } from '@/components/shared/AntdSelect';
import {
  depositTokenToL2Account,
  switchToChain,
  CHAINS_DICT,
  isMatchNetVersion,
  formatNum,
} from '@/utils';

import { useBridgeTokensList, useSupportedDepositToken, invalidL2History } from '../../../hooks';
import { queryRechargeTx } from '../../../utils';
import { AccountModal } from '../AccountModal';
import style from './index.module.css';

const MIN_AMOUNT = 1;
const MAX_AMOUNT = 1000;

type Props = { turnOff(): void; depositTo?: { chain: string; id: string } };

const useDeposit = (onClose: Props['turnOff'], depositTo: Props['depositTo']) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [hideButton, setHideButton] = useState(false);
  const [needSwitchChain, setNeedSwitchChain] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [successText] = useState('');
  const { address } = useCurrentAddressInfo();
  const [selectedTokenId, setSelectedTokenId] = useState<string>();
  const [buttonText, setButtonText] = useState(t('DepositModal.confirm-deposit'));
  const { data: remoteL2BridgeTokenList } = useSupportedDepositToken();
  const { tokenList, isAmountLoading } = useBridgeTokensList(address, remoteL2BridgeTokenList);

  const selectedToken = useMemo(
    () => tokenList?.find(x => `${x.chain}_${x.id}` === selectedTokenId),
    [selectedTokenId, tokenList],
  );

  const usdcAmount = useMemo(
    () => Math.floor((selectedToken?.amount || 0) * 100) / 100,
    [selectedToken?.amount],
  );

  useEffect(() => {
    setHideButton(needSwitchChain);
  }, [needSwitchChain]);

  useEffect(() => {
    if (!isAmountLoading && tokenList?.length && !selectedTokenId) {
      if (depositTo && tokenList?.some(x => x.chain === depositTo.chain && x.id === depositTo.id)) {
        setSelectedTokenId(`${depositTo.chain}_${depositTo.id}`);
        return;
      }
      setSelectedTokenId(`${tokenList[0].chain}_${tokenList[0].id}`);
    }
  }, [depositTo, isAmountLoading, tokenList, selectedTokenId]);

  const { control, setValue } = useForm({
    mode: 'onChange',
  });

  const amount = useWatch({
    control,
    name: 'amount',
  });

  useEffect(() => {
    const isValid = Number(amount) >= MIN_AMOUNT && Number(amount) <= usdcAmount;
    setIsButtonDisabled(!isValid);
    if (amount && Number(amount) > MAX_AMOUNT) {
      setValue('amount', Math.min(usdcAmount, MAX_AMOUNT), {
        shouldValidate: true,
      });
      message.info(t('DepositModal.maxTip', { MAX_AMOUNT }), 6);
    }
  }, [amount, setValue, usdcAmount, t]);

  const validateInput = useCallback(
    (amount: string) => {
      const isValid = Number(amount) >= MIN_AMOUNT && Number(amount) <= usdcAmount;
      return isValid;
    },
    [usdcAmount],
  );

  const insufficientBalance = useMemo(
    () => amount && Number(amount) > usdcAmount,
    [amount, usdcAmount],
  );

  const resetStatus = useCallback(() => {
    setIsLoading(false);
    setButtonText(t('DepositModal.confirm-deposit'));
  }, [t]);

  const chainName = useMemo(
    () => selectedToken && CHAINS_DICT[selectedToken.chain]?.name,
    [selectedToken],
  );

  const tryDeposit = useCallback(async () => {
    if (!selectedToken) {
      return;
    }
    const isMatch = await isMatchNetVersion(selectedToken.chain);
    if (!isMatch) {
      setNeedSwitchChain(true);
      return;
    }
    setIsLoading(true);
    setButtonText(t('DepositModal.confirming'));
    const decimals = selectedToken.decimals;
    depositTokenToL2Account(selectedToken.id!, address!, amount, selectedToken.chain, decimals)
      .then(tx => {
        const pollingQueryTx = () => {
          queryRechargeTx(tx as string).then((res: { success: boolean }) => {
            if (res?.success) {
              window.clearInterval(timer);
              message.info(t('DepositModal.confirming'), 4);
              resetStatus();
              invalidL2History();
              onClose();
            }
          });
        };
        const timer = window.setInterval(pollingQueryTx, 2500);
      })
      .catch(e => {
        console.warn('deposit cancel error', e);
        resetStatus();
      });
  }, [address, amount, onClose, resetStatus, selectedToken, t]);

  const steps = [
    {
      handler: async (next: () => void) => {
        tryDeposit();
      },
      textWidth: 64,
      content: (
        <div className={style.deposit}>
          {needSwitchChain ? (
            <>
              <div className={style.switchChainPage}>
                <div className={style.text}>{t('DepositModal.1wveyxo', { chain: chainName })}</div>
                <div className={style.text}>{t('DepositModal.1enenmq', { chain: chainName })}</div>
              </div>
              <div className={style.footer}>
                <Button
                  className={cx(style.button, style.normalBtn)}
                  onClick={() => setNeedSwitchChain(false)}>
                  {t('DepositModal.x9d2fu')}
                </Button>
                <Button
                  type={'primary'}
                  className={style.button}
                  onClick={async () => {
                    try {
                      await switchToChain(selectedToken?.chain);
                      setNeedSwitchChain(false);
                      tryDeposit();
                    } catch (error) {
                      console.warn('switch error', error);
                    }
                  }}>
                  {t('DepositModal.1og9epz', { chain: chainName })}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className={style.depositItem}>
                <div className={style.title}>{t('WithdrawModal.2phgkn')}</div>
                <div className={style.content}>
                  <div className={style.selectToken}>
                    <AntdSelect
                      dropdownClassName={style.selectTokenList}
                      select={selectedTokenId}
                      width={'400px'}
                      height={'52px'}
                      placeholder={t('WithdrawModal.h4xbw6')}
                      selectOptions={tokenList.map(token => ({
                        mode: `${token.chain}_${token.id}`,
                        label: (
                          <div className={style.selectOption}>
                            <TokenAvatar
                              className={style.selectOptionIcon}
                              logo={token.logo_url}
                              chain={token.chain}
                              size={24}
                              chainSize={14}
                            />
                            <div className={style.tokenSymbol}>{token.optimized_symbol}</div>
                            <div className={style.tokenBalance}>
                              {t('DepositModal.1revpwb')}{' '}
                              {isAmountLoading
                                ? '-'
                                : formatNum(token?.amount || 0, 2, { truncate: true })}
                            </div>
                          </div>
                        ),
                      }))}
                      setSelect={setSelectedTokenId}
                    />
                  </div>
                </div>
              </div>
              <div className={cx(style.depositItem, style.amountItem)}>
                <div className={cx(style.title, style.amountTitle)}>
                  {t('DepositModal.1ts777e')}
                </div>
                <div className={style.content}>
                  <div
                    className={cx(
                      style.currentAmount,
                      insufficientBalance && style.insufficientBalance,
                    )}>
                    {insufficientBalance
                      ? t('DepositModal.insufficient')
                      : t('DepositModal.1revpwb')}{' '}
                    {isAmountLoading ? <Loading /> : selectedToken ? usdcAmount : '--'}
                  </div>
                  <Controller
                    name="amount"
                    control={control}
                    rules={{ min: MIN_AMOUNT, required: true, validate: validateInput }}
                    render={({ field }) => (
                      <Input
                        className={style.tokenAmountInput}
                        placeholder="1.00-1000.00"
                        disabled={isLoading}
                        {...field}
                      />
                    )}
                  />
                </div>
              </div>
              <div className={style.depositItem}>
                <div className={style.title}> </div>
                <div className={style.depositTip}>
                  <div>{t('DepositModal.kkfypj')}</div>
                </div>
              </div>
            </>
          )}
        </div>
      ),
    },
  ];

  return {
    steps,
    isLoading,
    successText,
    buttonText,
    isButtonDisabled,
    hideButton,
  };
};

export const DepositModal = ({ turnOff, depositTo }: Props) => {
  const { t } = useTranslation();
  const { steps, isLoading, successText, buttonText, isButtonDisabled, hideButton } = useDeposit(
    turnOff,
    depositTo,
  );

  return (
    <AccountModal
      hideSteps
      headerTitle={t('DepositModal.depositTitle')}
      turnOff={turnOff}
      steps={steps}
      containerClass={style.modalContainer}
      isLoading={isLoading}
      successText={successText}
      buttonText={buttonText}
      isButtonDisabled={isButtonDisabled}
      hideButton={hideButton}
    />
  );
};
